import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const SPINNER_SIZE_CLASSES = {
  sm: "size-3.5",
  default: "size-4",
  lg: "size-5",
};

const LOADER_SIZE_CLASSES = {
  sm: "w-4 h-4",
  default: "w-8 h-8",
  lg: "w-12 h-12",
};

const CONTAINER_SIZES = {
  sm: "h-10 w-10",
  default: "h-16 w-16",
  lg: "h-20 w-20",
};

const PADDING_SIZES = {
  sm: "p-2",
  default: "p-3",
  lg: "p-4",
};

interface SpinnerProps extends React.SVGAttributes<SVGSVGElement> {
  size?: "sm" | "default" | "lg";
}

/** Spinner compacto para botones y controles inline. */
export function Spinner({ size = "sm", className, ...props }: SpinnerProps) {
  const sizeClasses = SPINNER_SIZE_CLASSES;

  return (
    <Loader2
      role="status"
      aria-label="Cargando"
      className={cn("animate-spin shrink-0", sizeClasses[size], className)}
      {...props}
    />
  );
}

interface LoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "default" | "lg";
  text?: string;
  /** Fuerza el spinner compacto (sin anillo decorativo). */
  inline?: boolean;
}

export function Loader({
  size = "default",
  text,
  inline,
  className,
  ...props
}: LoaderProps) {
  const useInline = inline ?? (size === "sm" && !text);

  if (useInline) {
    return <Spinner size="sm" className={className} />;
  }

  return (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)} {...props}>
      <div className={cn("relative flex items-center justify-center", CONTAINER_SIZES[size])}>
        <div className="absolute inset-0 rounded-full border border-primary/30 animate-pulse-ring" />
        <div
          className={cn(
            "relative rounded-full border border-primary/30 bg-primary/10 backdrop-blur-sm",
            PADDING_SIZES[size],
          )}
        >
          <Loader2 className={cn("animate-spin text-primary", LOADER_SIZE_CLASSES[size])} />
          <div className="absolute inset-0 -z-10 rounded-full bg-primary/20 blur-md" />
        </div>
      </div>
      {text && (
        <p className="text-sm text-muted-foreground/80 animate-pulse">{text}</p>
      )}
    </div>
  );
}
